import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { Typography } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { MoreHorizontal, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/** The grid every collection of cards sits in: templates, signatures. */
export function SettingsCardGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  )
}

interface SettingsCardProps {
  title: string
  /** Above the title, e.g. the signature image in a `SettingsCardMedia`. */
  media?: ReactNode
  /** One muted line under the title. */
  meta?: string
  badges?: ReactNode
  /** Top right: a `SettingsCardMenu`. */
  menu?: ReactNode
  /** Bottom: the card's main action. */
  footer?: ReactNode
}

/** One item of a card collection. Every collection uses this same card, so a
 *  template and a signature read the same way. */
export function SettingsCard({
  title,
  media,
  meta,
  badges,
  menu,
  footer,
}: SettingsCardProps) {
  return (
    <Card className="flex flex-col gap-3 p-4">
      {media}
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Typography as="small" className="line-clamp-2 leading-snug">
            {title}
          </Typography>
          {meta && <Typography as="muted">{meta}</Typography>}
        </div>
        {menu}
      </div>
      {badges && <div className="flex flex-wrap gap-1.5">{badges}</div>}
      {footer && <div className="mt-auto flex gap-2 pt-1">{footer}</div>}
    </Card>
  )
}

/** A card's image on a white ground: the background it gets on a PDF. */
export function SettingsCardMedia({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-24 items-center justify-center rounded-md bg-white">
      {children}
    </div>
  )
}

export interface SettingsCardMenuItem {
  label: string
  icon: LucideIcon
  onSelect: () => void
  disabled?: boolean
  destructive?: boolean
}

/** The card's "…" menu with its secondary actions. */
export function SettingsCardMenu({
  label,
  items,
  pending = false,
}: {
  /** Accessible name of the trigger, e.g. "Template actions". */
  label: string
  items: SettingsCardMenuItem[]
  /** An action picked from the menu is running: the trigger spins and
   *  takes no second pick until it is done. */
  pending?: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-busy={pending}
          disabled={pending}
          className="size-8 shrink-0 text-muted-foreground"
        >
          {pending ? <Spinner /> : <MoreHorizontal />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map(({ label, icon: Icon, onSelect, disabled, destructive }) => (
          <DropdownMenuItem
            key={label}
            disabled={disabled}
            onSelect={onSelect}
            className={cn(
              destructive && "text-destructive focus:text-destructive",
            )}
          >
            <Icon />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
